import React, { useRef, useState } from "react";
import { Upload, Trash2 } from "lucide-react";
import { Avatar } from "./ui/kit";
import OrezObrazku from "./OrezObrazku";
import { oriznoutLogo } from "../lib/logo";

/**
 * Nahrání fotky nebo loga z počítače / telefonu. Po výběru se otevře výřez
 * (posun tahem, přiblížení) a výsledek se uloží jako data URI do profilu —
 * zobrazí se pak všude, i v PDF nabídek a protokolů. `tvar="foto"` = čtverec
 * 256 px (JPEG), `tvar="logo"` = logo bez rámečku, jen s oříznutými okraji (PNG).
 * SVG se nechává, jak je (vektor by se výřezem rozmazal).
 */
const precist = (soubor) => new Promise((resolve, reject) => {
  const ctecka = new FileReader();
  ctecka.onerror = () => reject(new Error("Soubor nejde přečíst."));
  ctecka.onload = () => resolve(ctecka.result);
  ctecka.readAsDataURL(soubor);
});

export default function NahratObrazek({ value, onChange, tvar = "foto", jmeno = "", disabled = false, testid = "obrazek" }) {
  const vstup = useRef(null);
  const [chyba, setChyba] = useState("");
  const [orez, setOrez] = useState(null);   // data URI vybraného obrázku, než se ořízne
  const vyber = async (e) => {
    const soubor = e.target.files?.[0];
    e.target.value = "";
    if (!soubor) return;
    setChyba("");
    if (!soubor.type.startsWith("image/")) return setChyba("Nahrát jde jen obrázek (PNG, JPG, SVG).");
    if (soubor.size > 15 * 1024 * 1024) return setChyba("Obrázek je větší než 15 MB.");
    try {
      const data = await precist(soubor);
      if (soubor.type === "image/svg+xml") onChange(data);
      // Logo firmy bez pevného rámečku: jen se oříznou prázdné okraje, tvar zůstane.
      else if (tvar === "logo") onChange(await oriznoutLogo(data));
      else setOrez(data);
    } catch (err) { setChyba(err.message); }
  };
  return (
    <div className="flex items-center gap-4" data-testid={testid}>
      {tvar === "foto" ? <Avatar name={jmeno} url={value || null} size={64} />
        : tvar === "budova" || tvar === "produkt" ? (
          <span className="h-16 w-24 rounded-icon border border-line-strong bg-white flex items-center justify-center overflow-hidden shrink-0">
            {value ? <img src={value} alt="Fotka" className={`h-full w-full ${tvar === "budova" ? "object-cover" : "object-contain"}`} />
              : <span className="t-meta text-muted">bez fotky</span>}
          </span>
        ) : tvar === "znak" ? (
          <span className="h-16 w-16 rounded-[16px] border border-line-strong bg-white flex items-center justify-center overflow-hidden shrink-0">
            {value ? <img src={value} alt="Logo" className="h-full w-full object-contain" />
              : <span className="t-meta text-muted text-center leading-tight">bez<br />loga</span>}
          </span>
        ) : (
          <span className="h-16 w-28 rounded-icon border border-line-strong bg-white flex items-center justify-center overflow-hidden shrink-0">
            {value ? <img src={value} alt="Logo" className="max-h-full max-w-full object-contain p-1.5" />
              : <span className="t-meta text-muted">bez loga</span>}
          </span>
        )}
      <div className="min-w-0">
        <div className="flex flex-wrap gap-2">
          <button type="button" onClick={() => vstup.current?.click()} disabled={disabled}
            className="btn-ghost tap h-9 px-3 text-[12.5px]" data-testid={`${testid}-nahrat`}>
            <Upload size={14} /> {value ? "Změnit" : ["foto", "budova", "produkt"].includes(tvar) ? "Nahrát fotku" : "Nahrát logo"}
          </button>
          {value && (
            <button type="button" onClick={() => onChange("")} disabled={disabled}
              className="tap h-9 px-2 t-meta font-600 text-muted hover:text-danger inline-flex items-center gap-1" data-testid={`${testid}-odebrat`}>
              <Trash2 size={13} /> Odebrat
            </button>
          )}
        </div>
        <p className="t-meta text-muted mt-1">{tvar === "logo" ? "PNG s průhledným pozadím nebo SVG, jakýkoli tvar. Prázdné okraje se oříznou samy."
          : tvar === "znak" ? "Čtvercové nebo kulaté logo. Po výběru ho přiblížíš a posuneš do čtverce."
            : tvar === "budova" ? "Fotka domu do PDF nabídky. Po výběru ji přiblížíš a posuneš."
              : tvar === "produkt" ? "Fotka zařízení do PDF nabídky, nejlépe na bílém pozadí."
            : "Po výběru fotku přiblížíš a posuneš."}</p>
        {chyba && <p className="t-meta text-danger mt-1">{chyba}</p>}
      </div>
      {orez && (
        <OrezObrazku src={orez} tvar={tvar} onZrusit={() => setOrez(null)}
          onHotovo={(data) => { setOrez(null); onChange(data); }} />
      )}
      <input ref={vstup} type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" className="hidden" onChange={vyber} />
    </div>
  );
}
